import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Car } from 'lucide-react';
import FilterPanel from '../components/FilterPanel';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import VehicleBanner from '../components/VehicleBanner';
import VehicleTyreSizesCard from '../components/VehicleTyreSizesCard';
import SortSelect, { sortProducts, useTopRatedFilter } from '../components/SortSelect';
import { Button } from '../components/ui/button';
import { productsAPI, cartAPI, categoriesAPI } from '../services/api';
import { vehicleStore, useSelectedVehicle } from '../services/vehicleStore';
import { usePageMeta } from '../services/seo';
import { useT } from '../services/i18n';
import { catName } from '../services/catalogI18n';

const PartsPage = () => {
  const { t, lang } = useT();
  const navigate = useNavigate();
  const vehicle = useSelectedVehicle();
  usePageMeta({
    title: vehicle ? t('pp.metaTitleVehicle', { v: `${vehicle.brand} ${vehicle.model} ${vehicle.engine?.name || ''}`.trim() }) : t('pp.metaTitle'),
    description: vehicle ? t('pp.metaDescVehicle', { v: `${vehicle.brand} ${vehicle.model} (${vehicle.engine?.name || ''})` }) : t('pp.metaDesc'),
    path: '/pieces',
  });
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('');
  const [sort, setSort] = useState('relevance');
  const [isLoading, setIsLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(null);

  useEffect(() => {
    categoriesAPI.getAll().then(setCategories).catch(() => setCategories([]));
  }, []);

  const loadProducts = useCallback(async () => {
    if (!vehicle) return;
    setIsLoading(true);
    try {
      const data = await productsAPI.getAll(null, null, vehicleStore.toParams(vehicle));
      setProducts(data);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setIsLoading(false);
    }
  }, [vehicle]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleAddToCart = async (productId) => {
    setAddingToCart(productId);
    try {
      await cartAPI.addItem(productId);
    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      setAddingToCart(null);
    }
  };

  const countByCategory = products.reduce((acc, p) => ({ ...acc, [p.category]: (acc[p.category] || 0) + 1 }), {});
  const topRatedFilter = useTopRatedFilter(activeCategory ? products.filter(p => p.category === activeCategory) : products);
  const visible = sortProducts(topRatedFilter.list, sort);

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6">
          <Link to="/" className="flex items-center gap-2 text-blue-600 hover:underline">
            <ArrowLeft size={18} />
            {t('pp.backHome')}
          </Link>
        </div>

        {!vehicle ? (
          <div data-testid="parts-no-vehicle" className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Car size={48} className="mx-auto text-gray-300 mb-4" />
            <h1 className="text-xl font-bold text-gray-900 mb-2">{t('pp.noVehicleTitle')}</h1>
            <p className="text-gray-500 mb-6">{t('pp.noVehicleText')}</p>
            <Button onClick={() => navigate('/')} className="bg-orange-500 hover:bg-orange-600">
              {t('pp.selectVehicle')}
            </Button>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('pp.title')}</h1>
            <VehicleBanner vehicle={vehicle} count={products.length} />
            <VehicleTyreSizesCard vehicle={vehicle} />

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              <FilterPanel label={t('pp.categories')} resultCount={visible.length} activeCount={activeCategory ? 1 : 0} onReset={() => setActiveCategory('')}>
                <div className="bg-white rounded-lg shadow-sm p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Catégories</h3>
                  <button
                    data-testid="parts-category-all"
                    onClick={() => setActiveCategory('')}
                    className={`w-full flex justify-between text-sm px-3 py-2 rounded-md mb-1 ${!activeCategory ? 'bg-orange-50 text-orange-700 font-medium' : 'hover:bg-gray-50 text-gray-700'}`}
                  >
                    <span>Toutes</span><span className="text-gray-400">{products.length}</span>
                  </button>
                  {categories
                    .filter(c => countByCategory[c.slug])
                    .map(c => (
                      <button
                        key={c.slug}
                        data-testid={`parts-category-${c.slug}`}
                        onClick={() => setActiveCategory(c.slug)}
                        className={`w-full flex justify-between text-sm px-3 py-2 rounded-md mb-1 ${activeCategory === c.slug ? 'bg-orange-50 text-orange-700 font-medium' : 'hover:bg-gray-50 text-gray-700'}`}
                      >
                        <span>{catName(c, lang)}</span><span className="text-gray-400">{countByCategory[c.slug]}</span>
                      </button>
                    ))}
                </div>
              </FilterPanel>

              <div className="lg:col-span-3">
                {!isLoading && <SortSelect value={sort} onChange={setSort} count={visible.length} topRated={topRatedFilter.topRated} />}
                {isLoading ? (
                  <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                  </div>
                ) : visible.length === 0 ? (
                  <div data-testid="parts-empty" className="bg-white rounded-lg shadow-sm p-12 text-center">
                    <p className="text-gray-500">Aucune pièce compatible trouvée pour ce véhicule</p>
                  </div>
                ) : (
                  <div data-testid="parts-grid" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {visible.map(product => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        compatible
                        onAddToCart={handleAddToCart}
                        isAdding={addingToCart === product.id}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default PartsPage;
